import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, LogOut, Coins, Menu, X } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useTokensSimple } from '../hooks/useTokensSimple'

export function Header() {
  const { user, userType, signOut } = useAuth()
  const { balance } = useTokensSimple()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
    setIsMenuOpen(false)
  }

  const handleProfileClick = () => {
    if (userType === 'model') {
      navigate('/model-dashboard')
    } else if (userType === 'client') {
      navigate('/client-profile')
    } else {
      navigate('/')
    }
    setIsMenuOpen(false)
  }

  return (
    <header className="bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent"
            >
              Next Level BR
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Início
            </Link>
            <Link
              to="/models"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Modelos
            </Link>
            {user && userType === 'client' && (
              <Link
                to="/tokens"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Comprar Tokens
              </Link>
            )}
          </nav>

          {/* User Section */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Token Balance */}
                {userType === 'client' && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-2 bg-pink-600/20 px-3 py-1 rounded-full border border-pink-500/30"
                  >
                    <Coins className="w-4 h-4 text-pink-400" />
                    <span className="text-pink-400 font-semibold">{balance}</span>
                  </motion.div>
                )}

                {/* Desktop User Menu */}
                <div className="hidden md:flex items-center space-x-2">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
                  >
                    <User className="w-5 h-5" />
                    <span>{user.user_metadata?.name || 'Usuário'}</span>
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden text-gray-300 hover:text-white transition-colors"
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Entrar
                </Link>
                <Link
                  to="/register"
                  className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Cadastrar
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && user && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden py-4 border-t border-gray-800"
          >
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-300 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Início
              </Link>
              <Link
                to="/models"
                className="text-gray-300 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Modelos
              </Link>
              {userType === 'client' && (
                <Link
                  to="/tokens"
                  className="text-gray-300 hover:text-white transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Comprar Tokens
                </Link>
              )}
              <button
                onClick={handleProfileClick}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors text-left"
              >
                <User className="w-5 h-5" />
                <span>Meu Perfil</span>
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 text-red-400 hover:text-red-300 transition-colors text-left"
              >
                <LogOut className="w-5 h-5" />
                <span>Sair</span>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </header>
  )
}
